import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Users, UserPlus, Check, X, LogOut, Loader2, Crown, Lock } from "lucide-react";

export const HouseholdCard = () => {
  const { user, refresh } = useAuth();
  const isPremium = !!user?.own_premium;
  const [data, setData] = useState(null);
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/household").then((r) => setData(r.data)).catch(() => setData({ members: [], incoming_invites: [], outgoing_invites: [] }));
  useEffect(() => { load(); }, []);

  const invite = async () => {
    if (!email.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post("/household/invite", { email: email.trim(), origin_url: window.location.origin });
      setEmail("");
      toast.success(data?.invited_new_user
        ? "Invite sent — we've emailed them a link to join the app"
        : "Invite sent");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not send invite"); } finally { setBusy(false); }
  };

  const accept = async (id) => { try { await api.post(`/household/invite/${id}/accept`); toast.success("Joined household"); await load(); await refresh(); } catch (e) { toast.error(e?.response?.data?.detail || "Could not accept"); } };
  const decline = async (id) => { await api.post(`/household/invite/${id}/decline`); await load(); };
  const leave = async () => { await api.post("/household/leave"); toast.success("Left household"); await load(); await refresh(); };
  const removeMember = async (m) => { try { await api.post(`/household/member/${m.id}/remove`); toast.success(`Removed ${m.name || m.email}`); await load(); await refresh(); } catch (e) { toast.error(e?.response?.data?.detail || "Could not remove"); } };
  const cancelInvite = async (id) => { try { await api.post(`/household/invite/${id}/cancel`); toast.success("Invite cancelled"); await load(); } catch (e) { toast.error(e?.response?.data?.detail || "Could not cancel"); } };

  if (!data) return null;
  const members = data.members || [];
  const incoming = data.incoming_invites || [];
  const outgoing = data.outgoing_invites || [];
  const inHousehold = !!data.household_id && members.length > 0;
  const full = members.length >= 2;
  const isOwner = !!data.household_id && user?.id === data.household_id;

  return (
    <section data-testid="household-card" className="rounded-3xl border border-black/8 bg-nerd-card p-6 sm:p-8 mb-8 animate-fade-up">
      <div className="flex items-center gap-2.5 mb-1">
        <Users className="w-5 h-5 text-nerd-light" />
        <h2 className="font-display text-xl font-extrabold tracking-tight">Shared household</h2>
      </div>
      <p className="text-sm text-nerd-light/55 mb-5">Share your meal planner, shopping list and pantry with one other person. Your favourites and saved recipes stay private to you. <span className="text-nerd-light/70 font-medium">Only you need Premium — the person you invite doesn't.</span></p>

      {incoming.map((inv) => (
        <div key={inv.id} data-testid={`invite-incoming-${inv.id}`} className="flex flex-col sm:flex-row sm:items-center gap-3 justify-between rounded-2xl bg-nerd-depth border border-mint/30 p-4 mb-4">
          <p className="text-sm text-nerd-light"><span className="font-semibold">{inv.from_name}</span> invited you to their household.</p>
          <div className="flex gap-2 shrink-0">
            <button data-testid={`invite-accept-${inv.id}`} onClick={() => accept(inv.id)} className="inline-flex items-center gap-1.5 bg-nerd-light text-white px-4 py-2 rounded-full text-sm font-semibold hover:opacity-90"><Check className="w-4 h-4" /> Accept</button>
            <button data-testid={`invite-decline-${inv.id}`} onClick={() => decline(inv.id)} className="inline-flex items-center gap-1.5 bg-nerd-card border border-black/10 px-4 py-2 rounded-full text-sm font-semibold hover:bg-black/5"><X className="w-4 h-4" /> Decline</button>
          </div>
        </div>
      ))}

      {inHousehold ? (
        <>
          <div className="space-y-2 mb-5">
            {members.map((m) => (
              <div key={m.id} data-testid={`household-member-${m.email}`} className="flex items-center gap-3 rounded-2xl bg-nerd-depth border border-black/8 p-3">
                {m.avatar_url ? <img src={m.avatar_url} alt={m.name} className="w-9 h-9 rounded-full object-cover" /> : <div className="w-9 h-9 rounded-full bg-nerd-light text-white flex items-center justify-center text-sm font-bold">{(m.name || m.email).charAt(0).toUpperCase()}</div>}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold truncate">{m.name || m.email}</p>
                  <p className="text-xs text-nerd-light/45 truncate">{m.email}</p>
                </div>
                {m.is_owner && <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-nerd-light/60"><Crown className="w-3.5 h-3.5" /> Owner</span>}
                {isOwner && !m.is_owner && (
                  <button data-testid={`household-remove-${m.email}`} onClick={() => removeMember(m)} title="Remove from household"
                    className="shrink-0 w-8 h-8 rounded-full border border-black/15 flex items-center justify-center text-nerd-light/50 hover:bg-coral hover:text-white hover:border-coral transition-colors">
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
            ))}
          </div>
          {outgoing.map((inv) => (
            <div key={inv.id} data-testid={`invite-outgoing-${inv.id}`} className="flex items-center gap-2 justify-between rounded-2xl bg-nerd-depth border border-black/8 p-3 mb-3">
              <p className="text-xs text-nerd-light/60 min-w-0 truncate">Pending invite to <span className="text-nerd-light/90 font-medium">{inv.to_email}</span></p>
              <button data-testid={`invite-cancel-${inv.id}`} onClick={() => cancelInvite(inv.id)} title="Cancel invite"
                className="shrink-0 inline-flex items-center gap-1.5 text-xs font-semibold text-nerd-light/60 hover:text-coral border border-black/15 hover:border-coral rounded-full px-3 py-1.5 transition-colors">
                <X className="w-3.5 h-3.5" /> Cancel
              </button>
            </div>
          ))}
          {!full && outgoing.length === 0 && isPremium && (
            <InviteRow email={email} setEmail={setEmail} onInvite={invite} busy={busy} />
          )}
          <button data-testid="household-leave-btn" onClick={leave} className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-nerd-light/60 hover:text-coral transition-colors"><LogOut className="w-4 h-4" /> Leave household</button>
        </>
      ) : (
        incoming.length === 0 && (
          isPremium
            ? <InviteRow email={email} setEmail={setEmail} onInvite={invite} busy={busy} />
            : <PremiumGate />
        )
      )}
    </section>
  );
};

const PremiumGate = () => (
  <div data-testid="household-premium-gate" className="flex items-center gap-3 rounded-2xl bg-nerd-depth border border-black/8 p-4">
    <div className="w-9 h-9 rounded-full bg-nerd-light text-white flex items-center justify-center shrink-0"><Lock className="w-4 h-4" /></div>
    <p className="text-sm text-nerd-light/70">Sharing a household is a <span className="font-semibold text-nerd-light">Premium</span> feature. Upgrade to invite someone — they can join and share your planner, shopping list and pantry for free.</p>
  </div>
);

const InviteRow = ({ email, setEmail, onInvite, busy }) => (
  <div className="flex flex-col sm:flex-row gap-2.5">
    <input data-testid="household-invite-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="partner@email.com"
      className="flex-1 bg-nerd-depth border border-black/10 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-mint" />
    <button data-testid="household-invite-btn" onClick={onInvite} disabled={busy}
      className="inline-flex items-center justify-center gap-2 bg-nerd-light text-white px-5 py-2.5 rounded-xl text-sm font-semibold hover:opacity-90 disabled:opacity-60 transition-opacity">
      {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />} Send invite
    </button>
  </div>
);
